import { useId } from "react";
import { setBackName } from "#app/utils/wear/lettering.ts";
import mockup from "#app/utils/wear/mockup.json";

// The AI back mockup with its name and hem line removed (brand/out/web/wear). Units are its pixels.
const { back, name, hem, mesh, thumb } = mockup;

/**
 * The buyer's word on the back of the mockup: printed cream ink on the nameplate, with a soft shadow and the
 * mesh showing faintly through (names are printed, not tackle twill), plus the hem line in lowercase.
 */
export function BackPreview({
  printed,
  term,
  crop = "full",
}: {
  printed?: string;
  term?: string;
  /** "thumb" shows just the nameplate and the 47, from a small image (the word-page card, spec 02). */
  crop?: "full" | "thumb";
}) {
  const id = useId();
  const set = printed ? setBackName(printed) : null;
  // Same coordinates either way: a thumbnail is just a smaller viewBox over the back, with its own image.
  const box =
    crop === "thumb"
      ? thumb
      : { x: 0, y: 0, width: back.width, height: back.height };
  const label = printed
    ? `Black jersey, back, with ${printed} above 47`
    : "Black jersey, back, with space for your word above 47";

  return (
    <svg
      viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
      role="img"
      aria-label={label}
      className="block h-auto w-full"
    >
      <defs>
        <pattern
          id={`${id}-mesh`}
          patternUnits="userSpaceOnUse"
          x={mesh.x}
          y={mesh.y}
          width={mesh.size}
          height={mesh.size}
        >
          <image
            href="/images/wear/mesh-tile.png"
            width={mesh.size}
            height={mesh.size}
          />
        </pattern>
        <filter id={`${id}-shadow`} x="-5%" y="-20%" width="110%" height="150%">
          <feDropShadow
            dx="0"
            dy="1.5"
            stdDeviation="1.5"
            floodColor="#000"
            floodOpacity="0.55"
          />
        </filter>
      </defs>

      <image
        href={
          crop === "thumb"
            ? "/images/wear/back-black-thumb.webp"
            : "/images/wear/back-black-blank.webp"
        }
        x={box.x}
        y={box.y}
        width={box.width}
        height={box.height}
      />

      {set ? (
        <g
          transform={`translate(${name.centreX - set.width / 2} ${name.base})`}
          stroke={name.colour}
          strokeWidth={set.strokeWidth}
          strokeLinejoin="miter"
          strokeMiterlimit={1.6}
        >
          <path d={set.d} fill={name.colour} filter={`url(#${id}-shadow)`} />
          <path
            d={set.d}
            fill={`url(#${id}-mesh)`}
            stroke={`url(#${id}-mesh)`}
            opacity={0.45}
            style={{ mixBlendMode: "overlay" }}
          />
        </g>
      ) : (
        <g aria-hidden>
          <rect
            x={name.centreX - name.maxWidth / 2}
            y={name.base - name.capPx - 4}
            width={name.maxWidth}
            height={name.capPx + 8}
            rx={4}
            fill="none"
            stroke={name.colour}
            strokeWidth={2}
            strokeDasharray="8 6"
            opacity={0.7}
          />
          <text
            x={name.centreX}
            y={name.base - name.capPx / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fill={name.colour}
            fontFamily="Inter, var(--sans)"
            fontWeight={600}
            fontSize={20}
            letterSpacing={4}
          >
            YOUR WORD
          </text>
        </g>
      )}

      {crop === "full" ? (
        <text
          x={hem.centreX}
          y={hem.base}
          textAnchor="middle"
          fill={hem.colour}
          fontFamily="Inter, var(--sans)"
          fontWeight={600}
          fontSize={hem.sizePx}
          opacity={term ? 1 : 0.6}
          aria-hidden
        >
          {`${term ? term.trim().toLowerCase() : "your word"} · shonaslang.com`}
        </text>
      ) : null}
    </svg>
  );
}
